import { pluck, sum } from 'ramda';
import * as React from 'react';
import { Button, Icon, Modal, TextBox } from '../..';
import { showAlert, useAuth, User } from '../../../context/authContext';
import {
  storePreferenceFormAction,
  TalliedSalesPayment,
} from '../../../context/storeContext';
import { formatErrors } from '../../../utils/request';
import roundUp from '../../../utils/roundUp';
import setToLocalStorage from '../../../utils/setToLocalStorage';

type ModalProps = {
  onCloseModal: () => void;
  closeRegisterCb: () => void;
  currencySymbol: string;
  salesPayments: TalliedSalesPayment[];
  registerHistoryId: number;
  cashRegisterId: number;
  storeId: number;
};

function CloseRegisterPrompt({
  salesPayments,
  onCloseModal,
  closeRegisterCb,
  registerHistoryId,
  cashRegisterId,
  currencySymbol,
  storeId,
}: ModalProps) {
  const [{ user }, dispatch] = useAuth();
  const [status, setStatus] = React.useState<
    'IDLE' | 'LOADING' | 'RESOLVED' | 'REJECTED'
  >('IDLE');
  const [notes, setNotes] = React.useState('');

  const cashCount = React.useMemo(() => {
    const totalCounted = sum(
      pluck('counted', salesPayments).map(count =>
        Number.isNaN(count) ? 0 : count,
      ),
    );

    const totalExpected = sum(pluck('expected', salesPayments));

    return {
      counted: totalCounted,
      totalDifference: (totalCounted || 0) - totalExpected,
    };
  }, [salesPayments]);

  const handleCloseRegister = async () => {
    if (!cashRegisterId || !registerHistoryId || !salesPayments || !user) {
      return;
    }

    try {
      setStatus('LOADING');

      const { cashRegister: getCashRegister } =
        (await storePreferenceFormAction(
          `/close-register?store=${storeId}`,
          'put',
          { cashRegisterId, registerHistoryId, salesPayments, storeId, notes },
        )) as any;

      const updateUserStoreCashRegister = {
        ...user,
        ...(user.defaultStore && {
          defaultStore: {
            ...user.defaultStore,
            cashRegisters: user.defaultStore.cashRegisters.map(fndRegister => {
              if (fndRegister.id === cashRegisterId) {
                return getCashRegister;
              }
              return fndRegister;
            }),
          },
        }),
      } as User;

      setStatus('RESOLVED');

      dispatch({ type: 'SET_AUTH_USER', payload: updateUserStoreCashRegister });

      setToLocalStorage(
        '_pos_usrid',
        JSON.stringify(updateUserStoreCashRegister),
      );

      closeRegisterCb();

      onCloseModal();
    } catch (error) {
      setStatus('REJECTED');
      const { message } = formatErrors(error);
      showAlert(dispatch, {
        show: true,
        message,
        type: 'ERROR',
      });
    }
  };

  return (
    <Modal onClose={onCloseModal} show>
      <div className="rounded-md bg-white tablet:w-[500px]">
        <div className="flex flex-col p-5">
          <h3
            className="text-pos-black text-xl font-semibold mb-2 
            inline-flex whitespace-pre"
          >
            Close Register!
          </h3>
          <p className="text-pos-black text-base font-normal">
            Are you sure you want to close this register?
          </p>

          <div className="mt-4">
            <TextBox
              id="note"
              name="note"
              rows={3}
              placeholder="Add Closing Note"
              value={notes}
              onChange={event => setNotes(event.target.value)}
            />
          </div>

          {cashCount.totalDifference < 0 && (
            <span
              className="inline-flex items-center rounded-[4px] text-sm pl-2 pr-1 py-2 my-2 font-light 
              bg-red-200 text-pos-red leading-normal"
            >
              <Icon id="iconHintHelp" className="w-[17px] h-[17px] mr-2" />
              <span>
                You have a cash short (missing) of{' '}
                <strong className="inline-flex font-semibold">
                  {currencySymbol}
                  {roundUp(Math.abs(cashCount.totalDifference), 2).toFixed(2)}
                </strong>
                . This action cannot be reverted.
              </span>
            </span>
          )}

          <div className="mt-4 text-center flex flex-row ml-auto space-x-4">
            <Button
              label="Yes! Close Register"
              onClick={handleCloseRegister}
              type="button"
              disabled={status === 'LOADING'}
              loading={status === 'LOADING'}
              className={`${
                cashCount.totalDifference < 0 ? '!bg-pos-red ' : '!bg-pos-green'
              } border border-[rgba(27,31,36,0.15)] min-w-[180px]  h-[40px]
                    items-center justify-center text-white text-xbase
                    shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium`}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default CloseRegisterPrompt;
